import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import SafeImage from "@/components/base/SafeImage";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { allNews, formatDate, hotMovies, newsCategories, shortDate } from "@/lib/catalog";

const PER_PAGE = 5;

export default function NewsPage() {
  useDocumentMeta({
    title: "资讯与影评 - 日本情色电影行业动态",
    description:
      "日本情色电影资讯频道：整理行业动态、修复重映消息、摄影与声音专题，以及编辑撰写的影片影评。按时间倒序更新，支持类别筛选。",
    keywords: "日本电影资讯,日本情色电影,影评,行业动态,修复重映,专题分析",
  });

  const [activeCategory, setActiveCategory] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list =
      activeCategory === "全部" ? allNews : allNews.filter((item) => item.category === activeCategory);
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeCategory]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const topMovies = hotMovies(3);

  const changeCategory = (category: string) => {
    setActiveCategory(category);
    setPage(1);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="w-full border-b border-background-300/50 bg-background-100/50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumb items={[{ label: "资讯" }]} />
            <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="text-[11px] tracking-widest2 text-accent-500 font-label">NEWSROOM</p>
                <h1 className="mt-2 font-heading text-2xl font-bold text-foreground-50 md:text-4xl">
                  资讯与影评
                </h1>
                <p className="mt-3 max-w-2xl text-[13px] leading-relaxed text-foreground-400">
                  行业动态、修复重映、技术专题与影评。我们只写文字：从摄影、声音到发行，记录这个类型片如何被制作与被观看。
                </p>
              </div>
              <Link
                to="/"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 px-4 py-2 text-[12px] text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
              >
                <i className="ri-home-4-line" />
                返回首页
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
            <div>
              <div className="flex flex-wrap items-center gap-1 rounded-full border border-background-300/70 p-1">
                {newsCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => changeCategory(category)}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] transition-colors font-label ${
                      activeCategory === category
                        ? "bg-accent-500 text-background-50"
                        : "text-foreground-400 hover:text-foreground-100"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              <p className="mt-4 text-[11px] text-foreground-600 font-label">
                共 {filtered.length} 篇 · 第 {Math.min(page, Math.max(1, totalPages))} / {Math.max(1, totalPages)} 页
              </p>

              {pageItems.length === 0 ? (
                <div className="mt-8 rounded-lg border border-dashed border-background-400/60 bg-background-100/40 px-6 py-14 text-center">
                  <i className="ri-article-line text-3xl text-foreground-600" />
                  <h2 className="mt-3 font-heading text-base font-semibold text-foreground-100">该类别暂无文章</h2>
                  <button
                    type="button"
                    onClick={() => changeCategory("全部")}
                    className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-[12px] text-foreground-50 transition-colors hover:bg-primary-600 font-label"
                  >
                    查看全部资讯
                  </button>
                </div>
              ) : (
                <ul className="mt-6 flex flex-col divide-y divide-background-300/50 border-t border-background-300/50">
                  {pageItems.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/news/detail/${item.id}`}
                        className="group flex cursor-pointer flex-col gap-4 py-6 sm:flex-row sm:gap-6"
                      >
                        <div className="h-[160px] w-full shrink-0 overflow-hidden rounded-lg border border-background-300/50 bg-background-200 sm:h-[132px] sm:w-[210px]">
                          <SafeImage
                            src={item.cover}
                            alt={`${item.title} 配图`}
                            title={`${item.title} - 日本情色电影资讯`}
                            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] tracking-widest2 text-foreground-500 font-label">
                            <span className="text-accent-500">{item.category}</span>
                            <span>{shortDate(item.date)}</span>
                            <span className="text-foreground-700">{item.author}</span>
                          </div>
                          <h2 className="mt-2 font-heading text-[15px] font-semibold leading-snug text-foreground-100 transition-colors group-hover:text-accent-400 md:text-base">
                            {item.title}
                          </h2>
                          <p className="mt-2 line-clamp-2 text-[12px] leading-relaxed text-foreground-500 md:text-[13px]">
                            {item.excerpt}
                          </p>
                          <p className="mt-3 flex items-center gap-1 text-[11px] text-foreground-600 transition-colors group-hover:text-accent-400 font-label">
                            阅读全文
                            <i className="ri-arrow-right-line" />
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>

            <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg border border-background-300/60 bg-background-100 p-4">
                <h2 className="font-heading text-[13px] font-semibold text-foreground-100">热门标签</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["单厅影院", "胶片", "修复", "摄影", "声音设计", "地方影院", "影评", "取景地"].map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md border border-background-300/70 px-2.5 py-1 text-[11px] text-foreground-400 font-label"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-300/60 bg-background-100 p-4">
                <h2 className="font-heading text-[13px] font-semibold text-foreground-100">本周热门影片</h2>
                <ul className="mt-3 flex flex-col gap-3">
                  {topMovies.map((movie, index) => (
                    <li key={movie.id}>
                      <Link to={`/detail/${movie.id}`} className="group flex cursor-pointer gap-3">
                        <span className="font-display text-sm text-accent-500">0{index + 1}</span>
                        <span className="min-w-0">
                          <span className="block truncate font-heading text-[12px] text-foreground-200 transition-colors group-hover:text-accent-400">
                            {movie.title}
                          </span>
                          <span className="mt-0.5 block text-[10px] text-foreground-600 font-label">
                            {movie.year} · {movie.region}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-300/60 bg-background-100 p-4">
                <p className="text-[10px] tracking-widest2 text-foreground-500 font-label">EDITORIAL NOTE</p>
                <p className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                  本频道所有文章、受访者与事件均为演示虚构内容，用于展示资讯模块的排版结构。
                </p>
                <p className="mt-3 font-display text-[12px] text-foreground-600">最后整理：{formatDate("2024-12-06")}</p>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}